import {
  notifyAcceptedDialogMessage,
  notifyNewConversation,
} from '@utils/operatorNotificationRuntime'
import {
  hasSharedDialogClientSub,
  markSharedDialogUnaccepted,
} from '@utils/operatorDialogChannelSubscriptions'
import { isOperatorOutgoingEcho } from '@utils/operatorOutgoingEcho'
import {
  formatTelegramUsername,
  pickNonEmptyStr,
  resolveTelegramVisitorName,
} from '@utils/telegramVisitorName'


/**
 * Global (route-independent) operator notification coordinator.
 *
 * Historically all new-message / new-conversation notifications lived inside
 * `DialogsPage`, so leaving `/dialogs` unmounted the component and silenced every
 * incoming-message sound and browser notification. This module lets the always-mounted
 * `DialogsCountersProvider` fire the same notifications from the shared Centrifuge
 * connection while the dialogs page is not active.
 *
 * Coordination rules:
 *  - DialogsPage owns notifications only after it registers its hub listener
 *    (`dialogsPageOwnsNotifications === true`). Until then the provider keeps notifying
 *    so mount/init races never drop sounds.
 *  - While the page owns notifications, the provider stays silent for those channels.
 *  - A short cross-component dedupe window guards mount/unmount transitions so
 *    a single incoming message can never notify twice.
 *
 * Sound event mapping (when the page does not own notifications):
 *  - Inbox channel events → unaccepted-dialog sound (`notifyNewConversation`)
 *  - Per-dialog channel events: pass `soundBucket` from shared subscription registry
 *    (`unaccepted` → new_conversation, `accepted` → accepted_dialog)
 */

/** True only while DialogsPage is ready to handle inbox/dialog notifications itself. */
let dialogsPageOwnsNotifications = false

export function setDialogsPageOwnsNotifications(owns: boolean): void {
  dialogsPageOwnsNotifications = owns
}

/** @deprecated Use setDialogsPageOwnsNotifications */
export function setDialogsPageActive(active: boolean): void {
  setDialogsPageOwnsNotifications(active)
}

export function doesDialogsPageOwnNotifications(): boolean {
  return dialogsPageOwnsNotifications
}

/** @deprecated Use doesDialogsPageOwnNotifications */
export function isDialogsPageActive(): boolean {
  return doesDialogsPageOwnNotifications()
}

/**
 * When the dialogs page is mounted on the shared Centrifuge connection it registers a
 * hub listener so the provider forwards inbox channel events to the page's own handler
 * (preserving the exact on-page list/notification behavior). When no listener is
 * registered, the provider handles inbox events itself.
 */
export type InboxHubListener = (
  data: unknown,
  widgetId: string | undefined,
  source: 'subscribed' | 'publication',
) => void

let inboxHubListener: InboxHubListener | null = null

export function setDialogsInboxHubListener(listener: InboxHubListener | null): void {
  inboxHubListener = listener
}

export function getDialogsInboxHubListener(): InboxHubListener | null {
  return inboxHubListener
}

/**
 * Forward `operator.{id}.dialogs.feed` from DialogsCountersProvider → DialogsPage.
 * Server-side JWT pubs are handled on the provider connection; the page must not rely
 * solely on a second client `publication` listener (easy to miss after attach races).
 */
export type DialogsFeedHubListener = (data: unknown) => void

let dialogsFeedHubListener: DialogsFeedHubListener | null = null

export function setDialogsFeedHubListener(listener: DialogsFeedHubListener | null): void {
  dialogsFeedHubListener = listener
}

export function getDialogsFeedHubListener(): DialogsFeedHubListener | null {
  return dialogsFeedHubListener
}

/**
 * Transfer accept/decline list side-effects (drop from «Мои», open for accepter).
 * Provider invokes this after applying transfer notifications so DialogsPage updates
 * even when its own centrifuge publication route misses the event.
 */
export type TransferListEffectsListener = (data: unknown) => void

let transferListEffectsListener: TransferListEffectsListener | null = null

export function setTransferListEffectsListener(listener: TransferListEffectsListener | null): void {
  transferListEffectsListener = listener
}

export function notifyTransferListEffects(data: unknown): void {
  transferListEffectsListener?.(data)
}

// ── Cross-component dedupe ──────────────────────────────────────────────
const MESSAGE_DEDUPE_TTL_MS = 5000
const recentlyNotified = new Map<string, number>()

function pruneDedupe(now: number): void {
  if (recentlyNotified.size === 0) return
  for (const [key, expiresAt] of recentlyNotified) {
    if (expiresAt <= now) recentlyNotified.delete(key)
  }
}

export function messageNotificationKey(chatId: string, msgId: string, text: string): string {
  const id = String(chatId ?? '').trim()
  const normalizedText = String(text ?? '').trim()
  // Prefer text so inbox `dialog.updated` and dialog.* `message.created` for the same
  // visitor reply share one key (their message ids often differ or are synthetic).
  if (normalizedText) return `${id}:text:${normalizedText}`
  const mid = String(msgId ?? '').trim()
  if (mid && !/^(notify-|ws-|upd-|reopen-)/i.test(mid)) return `${id}:id:${mid}`
  return `${id}:empty`
}

/**
 * Claim the right to notify for a given message. Returns `true` for the first caller
 * within the dedupe window and `false` for subsequent duplicates. Both DialogsPage and
 * the provider call this so a message handled during a page transition notifies once.
 */
export function claimMessageNotification(key: string): boolean {
  const now = Date.now()
  pruneDedupe(now)
  if (recentlyNotified.has(key)) return false
  recentlyNotified.set(key, now + MESSAGE_DEDUPE_TTL_MS)
  return true
}

/** Mark a message as notified (used by DialogsPage which keeps its own primary dedupe). */
export function markMessageNotified(key: string): void {
  recentlyNotified.set(key, Date.now() + MESSAGE_DEDUPE_TTL_MS)
}

// ── WS payload parsing (faithful subset of DialogsPage logic) ───────────
function normalizeFrom(v: unknown): 'client' | 'agent' | 'system' {
  const s = String(v ?? '').toLowerCase()
  if (s === 'visitor' || s === 'client' || s === 'user') return 'client'
  if (s === 'agent' || s === 'operator' || s === 'admin' || s === 'manager') return 'agent'
  if (s === 'system') return 'system'
  return 'client'
}

function asObject(v: unknown): Record<string, any> | null {
  return v && typeof v === 'object' ? (v as Record<string, any>) : null
}

function extractVisitorName(chatId: string, ...candidates: unknown[]): string {
  for (const c of candidates) {
    const obj = asObject(c)
    if (!obj) continue
    const telegram = asObject(obj.telegram)
    const contact = asObject(obj.contact)
    const contactFormName = pickNonEmptyStr(contact?.name)
    const firstName = pickNonEmptyStr(
      telegram?.firstname,
      telegram?.firstName,
      telegram?.first_name,
      contact?.firstName,
      contact?.first_name,
    )
    const lastName = pickNonEmptyStr(
      telegram?.lastname,
      telegram?.lastName,
      telegram?.last_name,
      contact?.lastName,
      contact?.last_name,
    )
    const username = pickNonEmptyStr(
      telegram?.username,
      contact?.username,
      contact?.userName,
    )
    const userId = pickNonEmptyStr(
      telegram?.chatId,
      telegram?.chat_id,
      contact?.telegramChatId,
      obj.visitorId,
      obj.visitor_id,
    )
    const userLink = pickNonEmptyStr(
      telegram?.userLink,
      telegram?.user_link,
      telegram?.link,
      (() => {
        const display = pickNonEmptyStr(telegram?.displayName, telegram?.display_name)
        if (!display) return null
        const asUsername = formatTelegramUsername(username)
        if (asUsername && display === asUsername) return null
        if (username && display === username) return null
        return display
      })(),
    )
    const telegramLabel =
      telegram || contact?.telegramChatId || String(obj.type ?? '').toUpperCase() === 'TELEGRAM'
        ? resolveTelegramVisitorName({
            contactFormName,
            firstName,
            lastName,
            username,
            userLink,
            userId,
          })
        : null
    const name =
      telegramLabel ??
      contactFormName ??
      obj.visitorName ??
      contact?.name ??
      obj.visitor?.name ??
      obj.name ??
      obj.title
    if (name != null && String(name).trim() !== '') return String(name).trim()
  }
  return `Чат ${chatId}`
}

type IncomingMessage = {
  chatId: string
  from: 'client' | 'agent' | 'system'
  text: string
  messageId: string
  visitorName: string
}

function parseChannelChatId(channel: string): string {
  if (!channel.startsWith('dialog.')) return ''
  const rest = channel.slice('dialog.'.length)
  const dotIdx = rest.indexOf('.')
  return (dotIdx === -1 ? rest : rest.slice(0, dotIdx)).trim()
}

/**
 * Fire a notification for an incoming client message received on a per-dialog
 * (`dialog.<id>` / `dialog.<id>.internal`) channel. No-op unless it is a real client
 * message. Used by the provider only while the dialogs page is not active.
 *
 * `soundBucket` comes from the shared dialog subscription registry so inbox dialogs
 * keep the new_conversation sound off /dialogs (not the accepted_dialog sound).
 */
export function notifyFromDialogChannelEvent(
  channel: string,
  rawData: unknown,
  options?: { soundBucket?: 'unaccepted' | 'accepted' },
): void {
  const data = asObject(rawData)
  if (!data) return
  const chatId = parseChannelChatId(channel)
  if (!chatId) return

  // Some payloads nest the real event one level deeper ({ data: { type, ... } }).
  const root =
    data.type != null
      ? data
      : asObject(data.payload) ?? asObject(data.data) ?? data

  const eventType = String(root.type ?? data.type ?? '').toLowerCase()
  if (eventType.startsWith('dialog.transfer.')) return
  if (eventType === 'typing' || eventType === 'message.delivered' || eventType === 'message.read') return

  const inner = asObject(root.payload) ?? asObject(root.data) ?? root
  const isMessageEvent =
    eventType === 'chat.message.created' ||
    eventType === 'chat.internal_message.created' ||
    eventType === 'message' ||
    eventType === 'message.created' ||
    eventType === 'chat.message' ||
    eventType === 'dialogs.message'

  const messageRaw = asObject(inner.message) ?? inner
  const text = String(
    messageRaw.text ??
      messageRaw.answer ??
      messageRaw.body ??
      messageRaw.message ??
      messageRaw.lastMessage ??
      messageRaw.last_message ??
      messageRaw.lastMessageText ??
      messageRaw.last_message_text ??
      '',
  ).trim()

  // dialog.updated often carries visitor replies as lastMessage only — require an
  // explicit visitor sender so operator echoes do not trigger sounds.
  if (eventType === 'dialog.updated') {
    const fromRaw = messageRaw.from ?? messageRaw.source ?? messageRaw.user
    if (fromRaw == null || String(fromRaw).trim() === '') return
    if (normalizeFrom(fromRaw) !== 'client') return
    if (!text) return
  } else {
    const looksLikeMessage = isMessageEvent || text !== ''
    if (!looksLikeMessage) return

    const rawType = String(messageRaw.type ?? '').toUpperCase()
    const from =
      rawType === 'SYSTEM'
        ? 'system'
        : normalizeFrom(messageRaw.from ?? messageRaw.source ?? messageRaw.user)
    if (from !== 'client') return
  }

  const messageId = String(
    messageRaw.id ??
      messageRaw.messageId ??
      messageRaw.message_id ??
      messageRaw.lastMessageId ??
      messageRaw.last_message_id ??
      '',
  )
  if (text && isOperatorOutgoingEcho(chatId, text)) {
    return
  }

  const visitorName = extractVisitorName(chatId, messageRaw, inner, root, data)
  const bucket = options?.soundBucket ?? 'accepted'
  const from = 'client' as const

  if (bucket === 'unaccepted') {
    fireUnacceptedDialogMessage({ chatId, from, text, messageId, visitorName })
    return
  }

  fireAcceptedDialogMessage({ chatId, from, text, messageId, visitorName })
}

/**
 * Fire a notification for an inbox channel (`widget.<id>.dialogs.*`) event when it
 * represents a new conversation or a new incoming client message. Used by the provider
 * only while the dialogs page is not active.
 *
 * Inbox channel events are for unaccepted dialogs → unaccepted-dialog sound.
 */
export function notifyFromInboxEvent(rawData: unknown): void {
  const event = asObject(rawData)
  if (!event) return

  // Snapshots (initial state dumps) carry list arrays — never notify on those.
  const base = asObject(event.data) ?? event
  if (
    Array.isArray((base as any).inbox) ||
    Array.isArray((base as any).my) ||
    Array.isArray((base as any).archive) ||
    Array.isArray((base as any)?.dialogs?.inbox)
  ) {
    return
  }

  const type = String(event.type ?? base.type ?? '').toLowerCase()
  if (type.startsWith('dialog.transfer.')) return
  if (type === 'typing' || type === 'chat.archived' || type === 'dialog.archived') return
  if (type === 'message.delivered' || type === 'message.read') return

  const dataForMessage = asObject(base.data) ?? base
  const nestedMessage = asObject(dataForMessage.message)
  const dialogObj =
    asObject(base.dialog) ??
    asObject(base.chat) ??
    asObject(base.item) ??
    asObject(dataForMessage.dialog) ??
    asObject(dataForMessage.chat)
  const text = String(
    dataForMessage.text ||
      nestedMessage?.text ||
      dataForMessage.body ||
      dataForMessage.answer ||
      dataForMessage.lastMessage ||
      dataForMessage.last_message ||
      dataForMessage.lastMessageText ||
      dataForMessage.last_message_text ||
      dialogObj?.lastMessage ||
      dialogObj?.last_message ||
      dialogObj?.lastMessageText ||
      dialogObj?.last_message_text ||
      dialogObj?.preview ||
      (typeof dataForMessage.message === 'string' ? dataForMessage.message : '') ||
      base.text ||
      base.lastMessage ||
      '',
  ).trim()
  const chatId = String(
    event.chatId ??
      event.chat_id ??
      event.dialogId ??
      base.chatId ??
      base.chat_id ??
      base.dialogId ??
      base.id ??
      dataForMessage.chatId ??
      dataForMessage.chat_id ??
      dataForMessage.dialogId ??
      dataForMessage.id ??
      dialogObj?.id ??
      dialogObj?.chatId ??
      dialogObj?.chat_id ??
      '',
  ).trim()
  // Guard against treating a message-id as chat-id when both exist separately —
  // prefer explicit chat fields; `id` on a dialog.updated payload IS the chat id.
  if (!chatId) {
    return
  }

  const fromRaw =
    dataForMessage.from ??
    dataForMessage.source ??
    dataForMessage.user ??
    nestedMessage?.from ??
    nestedMessage?.source ??
    dialogObj?.lastMessageFrom ??
    dialogObj?.last_message_from
  const from = normalizeFrom(fromRaw)

  // Inbox channel is visitor-facing. dialog.updated often omits `from` entirely
  // (see production payloads) — treat missing sender as visitor, never as agent.
  if (from === 'agent' || from === 'system') return
  if (
    !text &&
    !/message|new_message|chat\.message|dialogs\.message|new_dialog|dialog\.new|inbox\.new|dialog\.updated/.test(
      type,
    )
  ) {
    return
  }
  if (type === 'dialog.updated' && !text) {
    return
  }

  const messageId = String(
    dataForMessage.id ??
      dataForMessage.messageId ??
      dataForMessage.message_id ??
      nestedMessage?.id ??
      dialogObj?.lastMessageId ??
      dialogObj?.last_message_id ??
      '',
  )
  const visitorName = extractVisitorName(chatId, dialogObj, dataForMessage, base)

  if (text && isOperatorOutgoingEcho(chatId, text)) {
    return
  }

  // Live dialog.* client sub owns the sound for that chat. Without a client sub,
  // inbox JWT still notifies (accepted dialogs would otherwise stay silent).
  if (hasSharedDialogClientSub(chatId)) {
    return
  }

  const key = messageNotificationKey(chatId, messageId, text)
  if (!claimMessageNotification(key)) return
  markSharedDialogUnaccepted(chatId)
  notifyNewConversation({ chatId, title: visitorName, preview: text || undefined })
}

function fireUnacceptedDialogMessage(msg: IncomingMessage): void {
  const key = messageNotificationKey(msg.chatId, msg.messageId, msg.text)
  if (!claimMessageNotification(key)) return
  notifyNewConversation({
    chatId: msg.chatId,
    title: msg.visitorName,
    preview: msg.text || undefined,
  })
}

function fireAcceptedDialogMessage(msg: IncomingMessage): void {
  const key = messageNotificationKey(msg.chatId, msg.messageId, msg.text)
  if (!claimMessageNotification(key)) return
  notifyAcceptedDialogMessage({
    chatId: msg.chatId,
    visitorName: msg.visitorName,
    messageText: msg.text,
  })
}
